import Reflux from 'reflux';
import { fromJS } from 'immutable';
import Rx from 'rxjs/Rx';
import { getPlainTextFromRawRTEState } from '@shoflo-rundown/draft-js-utils';
import _ from 'underscore';
import UserSessionStore from 'flux/stores/UserSessionStore';
import EventShowListStore from 'flux/stores/EventShowListStore';
import storeStateHelper from 'flux/stores/storeStateHelper';
import Messages from 'shared/constants/Messages';
import UIRegions from 'shared/constants/UIRegions';
import qs from 'query-string';
import urlHelper from 'shared/lib/urlHelper';
import {
  AutoScrollEnabledDefault,
  AutoScrollSpeeds,
  AutoScrollSpeedDefaultIndex,
  FullScreenEnabledDefault,
  LastScrollToIndexDefault,
  LineHeightPercentages,
  LineHeightPercentageDefaultIndex,
  populatePrompterSettingsWithDefaults,
  PrompterDisplayClocks,
  PrompterWidths,
  PrompterWidthDefaultIndex,
  ScrollToIndexDefault,
  ZoomPercentages,
  ZoomPercentageDefaultIndex,
} from 'shared/constants/FeaturedEditorConstants';

import ToastActions from 'flux/actions/ToastActions';
import AlertActions from 'flux/actions/AlertActions';
import RouteActions from 'flux/actions/RouteActions';
import CuesheetTrackingActions from 'flux/actions/cuesheet/CuesheetTrackingActions';
import CuesheetColumnActions from 'flux/actions/cuesheet/CuesheetColumnActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import IntercomActions from 'flux/actions/IntercomActions';
import FullScreenCuesheetActions from 'flux/actions/cuesheet.fullscreen/FullScreenCuesheetActions';
import RemoteTeleprompterActions from 'flux/actions/cuesheet.fullscreen/RemoteTeleprompterActions';
import RemoteTeleprompterStore from 'flux/stores/cuesheet.fullscreen/RemoteTeleprompterStore';
import FullScreenCuesheetStoreHelper from 'flux/stores/cuesheet.fullscreen/FullScreenCuesheetStoreHelper';
import UserActions from 'flux/actions/UserActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import CuesheetCueClockActions from 'flux/actions/cuesheet/CuesheetCueClockActions';
import {
  isTeleprompter,
  isRemoteTeleprompter,
  isDisplayView,
} from 'main/components/show/cuesheet/cuesheetViewHelper';
import { displayView as DisplayViewOnboarding } from 'shared/constants/Onboarding';
import { createEditorStateFromRawRTEState } from 'shared/lib/rteHelper';
import { convertToRaw } from 'draft-js';
import preloadItemSetImages from 'shared/flux/helpers/preloadItemSetImages';

const isPrompter = () => isTeleprompter() || isRemoteTeleprompter();

const { displayViewSelectColumn, displayViewSelectShowCaller } =
  DisplayViewOnboarding;

export default Reflux.createStore({
  ...storeStateHelper,

  listenables: [FullScreenCuesheetActions, RealTimeActions],

  init() {
    /**
     * Set up observable subjects
     */
    this.fullScreenObservables = {};
    this.fullScreenObservables.populateItemSetSubsetBuffer = new Rx.Subject();
    RemoteTeleprompterStore.listen(this._onRemoteTeleprompterStoreChange);

    this.listenTo(
      CuesheetColumnActions.updateUserSettingsPersistent.completed,
      'onUpdateUserSettingsPersistentCompleted',
    );

    /**
     * Subscriber function for observables...populate item set subset
     */
    const populateItemSetSubset = (requests) => {
      if (requests.length === 0) return;

      const indexOfLatestRequest = requests.length - 1;
      const { colIds } = requests[indexOfLatestRequest];
      let rowIds = [];
      let getAllRows = false;

      requests.forEach((request) => {
        if (request.rowIds.length === 0) getAllRows = true;
        rowIds = _.union(rowIds, request.rowIds);
      });

      if (getAllRows) rowIds = [];

      const { teamId, eventId, showId } = requests[indexOfLatestRequest];

      if (isRemoteTeleprompter()) {
        FullScreenCuesheetActions.populateItemSetSubsetForGuest(
          teamId,
          eventId,
          showId,
          colIds,
          rowIds,
        );
      } else {
        FullScreenCuesheetActions.populateItemSetSubset(
          teamId,
          eventId,
          showId,
          colIds,
          rowIds,
        );
      }
    };

    this.fullScreenObservables.populateItemSetSubsetBuffer
      .bufferTime(1000)
      .subscribe(populateItemSetSubset);

    this.setState(this._initState());

    this.listenTo(
      CuesheetColumnActions.saveFeaturedColumn,
      'onSaveFeaturedCuesheetColumn',
    );
    this.listenTo(
      CuesheetTrackingActions.populateShowCallers.completed,
      'onPopulateShowCallersCompleted',
    );
    this.listenTo(UserSessionStore, 'onUserSessionStoreChange');
    this.listenTo(EventShowListStore, 'onEventShowListStore');

    this.listenTo(
      RouteActions.goToTeleprompterInitialization,
      'onGoToTeleprompterInitialization',
    );
  },

  _isFullScreen() {
    return isDisplayView() || isPrompter();
  },

  _initState() {
    const next = {
      columns: [],
      displayClocks: PrompterDisplayClocks,
      loading: true,
      loadingColumnData: true,
      //auto scroll
      autoScrollSpeedIndex: AutoScrollSpeedDefaultIndex,
      autoScrollEnabled: AutoScrollEnabledDefault,

      fullScreenEnabled: FullScreenEnabledDefault,

      selectedCells: {},
      editingCell: {},
      rowPanel: true,

      lastScrollToIndex: LastScrollToIndexDefault,
      scrollToIndex: ScrollToIndexDefault,
      utilityPanel: true,
      userSettings: {},

      rowOrder: [],
      prompterWidthIndex: PrompterWidthDefaultIndex,

      trackingEnabled: false,
      trackableUsers: {},
      forceOnboardingWelcome: false,
    };

    return next;
  },

  onClear({ showId }) {
    const state = this.getState();

    const { prevColumnTitle, trackingEnabled, trackedUserId, showList } = state;

    if (isDisplayView()) {
      // no need to clear state if it's the same show
      if (showId === state.showId) return;
      const next = this._initState();

      if (prevColumnTitle) next.prevColumnTitle = prevColumnTitle;
      if (trackingEnabled) next.trackingEnabled = trackingEnabled;
      if (trackedUserId) next.trackedUserId = trackedUserId;
      if (showList) next.showList = showList;

      this.setStateAndTrigger(next);
    }
  },

  onEventShowListStore(state) {
    const shows = state.get('shows');
    this.setInState('showList', shows);
  },

  onUserSessionStoreChange(user) {
    const next = this.getState();

    user = user.toJS();

    this._user = user;

    this.setInState('userId', user._id);

    if (user.preferences) {
      let userSettings = next.userSettings || {};

      userSettings = Object.assign(userSettings, user.preferences);

      this.setInState('userSettings', userSettings);
    }

    if (this._onUserSessionPopulateCallback) {
      this._onUserSessionPopulateCallback();
      delete this._onUserSessionPopulateCallback;
    }

    if (this._onUserSessionShowCallerPopulateCallback) {
      this._onUserSessionShowCallerPopulateCallback();
      delete this._onUserSessionShowCallerPopulateCallback;
    }

    this._trackingContextChanged();
  },

  _trackingContextChanged() {
    if (!this._user) return;
    const user = this._user;

    const next = this.getState();

    const { eventId } = next;

    const { trackingState } = user;

    if (trackingState && trackingState[eventId]) {
      const trackedUserId = trackingState[eventId];

      next.trackedUserId = trackedUserId;

      if (isPrompter() || this._user.onboarding[displayViewSelectShowCaller]) {
        next.trackingEnabled = true;
      }

      this.setStateAndTrigger(next);
    }
  },

  onPopulate(teamId, eventId, showId) {
    this.mergeState({ teamId, eventId, showId });
    RealTimeActions.subscribeToEvent(eventId);
    RealTimeActions.subscribeToShow(showId, { teamId, eventId, showId });
    IntercomActions.updateSettings({ showId });
  },

  onPopulateCompleted(result) {
    if (!this.getInState('userId')) {
      this._onUserSessionPopulateCallback = () =>
        this._onPopulateCompleted(result);
      UserActions.loadFromSession();
    } else {
      this._onPopulateCompleted(result);
    }
  },

  onPopulateForRemotePrompter(teamId, eventId, showId) {
    this.mergeState({ teamId, eventId, showId });
    RealTimeActions.subscribeToShow(showId, { teamId, eventId, showId });
    IntercomActions.updateSettings({ showId });
  },

  onPopulateForRemotePrompterCompleted(result) {
    const { show } = result;
    if (show.disableRemotePrompter) return;
    this._onPopulateCompleted(result);
  },

  onToggleRemotePrompterCompleted(show) {
    const next = this.getState();
    next.show = show;
    next.showId = show._id;
    this.setStateAndTrigger(next);
  },

  //inital population doesn't include row data
  _onPopulateCompleted(result) {
    const { show, itemSetList } = result;

    const next = this.getState();
    next.showId = show._id;
    next.show = show;
    next.itemSetListId = itemSetList._id;

    const { columns, defaultColumnOrder, itemSets } = itemSetList;

    const itemSet = itemSets[0];

    const { teamId, eventId, showId } = next;

    next.columns = this._getFilteredColumns(columns, defaultColumnOrder);

    next.defaultColumnOrder = defaultColumnOrder;
    next.rowOrder = itemSet.rowOrder;

    next.columnId = this._getDefaultColumnId(next);

    next.prevColumnTitle = this._getColumnTitle(next.columnId, next);

    if (isDisplayView() && !this._user.onboarding[displayViewSelectColumn])
      delete next.columnId;

    if (isPrompter()) {
      const rowId = next.rowOrder[0];
      const params = {
        teamId,
        eventId,
        showId,
        rowId,
        columnId: next.columnId,
      };
      this._routeToTeleprompter(params, {});
    }

    next.loading = false;
    next.loadingPrompter = false;

    this.setStateAndTrigger(next);
    FullScreenCuesheetActions.populateShowCallers(teamId, eventId);
    FullScreenCuesheetActions.updateEditorDimensions();
  },

  _getDefaultColumnId(next) {
    const { columns, defaultColumnOrder } = next;

    const prevColumnId = this._getColumnIdByPreviousColumnTitle(columns, next);

    if (prevColumnId) return prevColumnId;

    const defaultColumnId = defaultColumnOrder[0];

    const matchedColumn = isPrompter()
      ? columns.find((column) => column.title.match(/(pa|script)/gi))
      : null;

    return matchedColumn ? matchedColumn._id : defaultColumnId;
  },

  _getColumnIdByPreviousColumnTitle(columns, state) {
    const next = state || this.getState();

    let { prevColumnTitle } = next;

    let columnId;
    let exactMatch;

    const exactSubsetMap = {};
    const partialMap = {};

    if (prevColumnTitle) {
      prevColumnTitle = prevColumnTitle.toLowerCase().trim();
      const prevTitleStrings = this._getSimplifiedStrings(prevColumnTitle);

      columns.some((column) => {
        const columnTitle = column.title.toLowerCase().trim();
        const colTitleStrings = this._getSimplifiedStrings(columnTitle);

        if (columnTitle === prevColumnTitle) {
          exactMatch = column._id;
        }

        prevTitleStrings.forEach((prevString) => {
          colTitleStrings.forEach((colString) => {
            if (prevString === colString) {
              if (!exactSubsetMap[column._id]) {
                exactSubsetMap[column._id] = [];
              }
              exactSubsetMap[column._id].push(`${prevString}:${colString}`);
            }

            if (prevString.length === 1 || colString.length === 1) return;

            if (prevString.includes(colString)) {
              if (!partialMap[column._id]) {
                partialMap[column._id] = [];
              }
              partialMap[column._id].push(`${prevString}:${colString}`);
            }

            if (colString.includes(prevString)) {
              if (!partialMap[column._id]) {
                partialMap[column._id] = [];
              }
              partialMap[column._id].push(`${colString}:${prevString}`);
            }
          });
        });

        return exactMatch;
      });

      if (exactMatch) {
        columnId = exactMatch;
      } else if (Object.keys(exactSubsetMap).length > 0) {
        let mostResults = 0;
        let exactSubsetColumnId;
        Object.keys(exactSubsetMap).forEach((id) => {
          if (exactSubsetMap[id].length > mostResults) {
            mostResults = exactSubsetMap[id].length;
            exactSubsetColumnId = id;
          }
        });
        columnId = exactSubsetColumnId;
      } else if (Object.keys(partialMap).length > 0) {
        let mostResults = 0;
        let partialMatchColumnId;
        Object.keys(partialMap).forEach((id) => {
          if (partialMap[id].length > mostResults) {
            mostResults = partialMap[id].length;
            partialMatchColumnId = id;
          }
        });
        columnId = partialMatchColumnId;
      }
    }

    return columnId;
  },

  _getSimplifiedStrings(str) {
    return str
      .replace(/[^a-zA-Z ]/g, '')
      .replace(/\s\s+/g, ' ')
      .split(' ');
  },

  onColumnUpdatedRealTime(showId, payload, clientId) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    const next = this.getState();

    const { columnId, changes } = payload;

    const { title } = changes;

    if (title) {
      const colIndex = next.columns.findIndex((column) => {
        return column._id === columnId;
      });
      const nextColumns = next.columns;
      nextColumns[colIndex].title = title;
      next.columns = [...nextColumns];
      this.setStateAndTrigger(next);
    }
  },

  onItemSetListColumnCreatedRealTime(
    showId,
    payload,
    responsePayload,
    clientId,
  ) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    this._addColumnTransient(responsePayload.column);
  },

  onItemSetListColumnRestoredRealTime(
    showId,
    payload,
    responsePayload,
    clientId,
  ) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    this._addColumnTransient(responsePayload.column);
  },

  _addColumnTransient(column) {
    const next = this.getState();

    next.columns.push(column);

    this.setStateAndTrigger(next);
  },

  onItemSetListColumnDeletedRealTime(
    showId,
    payload,
    responsePayload,
    clientId,
  ) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    this._deleteColumnTransient(payload.columnId);
  },

  _deleteColumnTransient(deletedColumnId) {
    const next = this.getState();

    const { columns } = next;

    const indexOfColumnToRemove = columns.findIndex((column) => {
      return column._id === deletedColumnId;
    });

    if (indexOfColumnToRemove === -1) return;

    next.columns.splice(indexOfColumnToRemove, 1);

    this.setStateAndTrigger(next);
  },

  onGlobalElementItemRowsAddedRealTime(
    showId,
    payload,
    responsePayload,
    clientId,
  ) {
    /**
     * Prevent row additons being applied to another show from being added to
     * the row list in the current store state. Listening for realtime row
     * additions means we may get add row actions from other shows.
     */
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    const { rows } = responsePayload;

    const rowIdsToRefresh = this._getRowIdsInState();

    rows.forEach((row) => {
      rowIdsToRefresh.push(row._id);
    });

    this._refreshRows(rowIdsToRefresh);
  },

  // eslint-disable-next-line no-unused-vars
  onItemSetListItemRowSettingsUpdatedRealTime(showId, payload, clientId) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;
    const rowIdsToRefresh = this._getRowIdsInState();
    this._refreshRows(rowIdsToRefresh);
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowsMovedRealTime(showId, payload, newRowOrder, clientId) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;
    const rowIdsToRefresh = this._getRowIdsInState();
    this._refreshRows(rowIdsToRefresh);
  },

  onItemRowMovedFromSourceToDestinationRealTime(
    params,
    payload,
    newRowOrder,
    clientId,
  ) {
    const { showId } = params;
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    const next = this.getState();

    next.rowOrder = [...newRowOrder];

    this.setStateAndTrigger(next);
  },

  onItemRowOrderUpdatedRealTime(showId, params, newRowOrder, clientId) {
    const { itemSetId } = params;

    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    if (isPrompter()) {
      const state = this.getState();
      let next =
        FullScreenCuesheetStoreHelper.itemRowOrderUpdatedRealTimeTeleprompter(
          itemSetId,
          newRowOrder,
          state,
        );
      this.setStateAndTrigger(next);
    } else {
      const rowIdsToRefresh = this._getRowIdsInState();
      this._refreshRows(rowIdsToRefresh);
    }
  },

  onItemRowsDeletedRealTime(showId, { itemSetId, rowIds }, clientId) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    const next = this.getState();

    if (isPrompter()) {
      this.setStateAndTrigger(
        FullScreenCuesheetStoreHelper.itemRowsDeletedRealTimeTeleprompter(
          itemSetId,
          rowIds,
          next,
        ),
      );
    } else {
      next.rowOrder = FullScreenCuesheetStoreHelper.deleteRowsFromRowOrder(
        itemSetId,
        rowIds,
        next.rowOrder,
      );
      next.rowMap = FullScreenCuesheetStoreHelper.deleteRowsFromRowMap(
        itemSetId,
        rowIds,
        next.rowMap,
      );
      this.setState(next);
      const rowIdsToRefresh = this._getRowIdsInState();
      this._refreshRows(rowIdsToRefresh);
    }
  },

  onItemRowsAddedRealTime(showId, payload, responsePayload, clientId) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    const { afterRowId, itemSetId, rows } = responsePayload;
    const next = this.getState();

    if (itemSetId !== next.itemSetId) return;

    if (isPrompter()) {
      this.setStateAndTrigger(
        FullScreenCuesheetStoreHelper.itemRowsAddedRealTimeTeleprompter(
          rows,
          afterRowId,
          next,
        ),
      );
    } else {
      const rowIdsToRefresh = this._getRowIdsInState();

      rows.forEach((row) => {
        rowIdsToRefresh.push(row._id);
      });

      this._refreshRows(rowIdsToRefresh);
    }
  },

  //publishing from GEM to show
  onPublishGlobalElementChangesRealTime(
    params,
    payload,
    responsePayload,
    clientId,
  ) {
    const { showId } = params;
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    const row = payload;
    row.item = row.item[0];

    const next = this.getState();
    const rowId = row._id;

    const { rowMap, itemSetId } = next;

    rowMap[itemSetId].rows[rowId] = row;

    next.rowMap = { ...rowMap };

    this.setStateAndTrigger(next);
  },

  //Retrieving global element changes from GEM
  onPublishLatestGlobalElementRowChangesToShowRowRealTime(
    showId,
    payload,
    responsePayload,
    clientId,
  ) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    const { globalElement } = payload;

    const itemSetId = globalElement.itemSet;

    const row = responsePayload;
    row.item = row.item[0];

    const next = this.getState();
    const rowId = row._id;

    const { rowMap } = next;

    rowMap[itemSetId].rows[rowId] = row;

    next.rowMap = { ...rowMap };

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowMutatedRealTime(showId, payload, responsePayload, clientId) {
    //If changes are made to a show outside of the global element manager
    //then do nothing. Only update the list for changes inside global element
    //manager

    const next = this.getState();

    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    const { changes } = payload;

    const alteredRowId = payload.rowId;
    const { itemSetId, rowOrder, rowMap, show } = next;

    if (Number.isInteger(changes.number) && show.autoCountCues) {
      const offset = changes.number - this._getRowIndexByRowId(alteredRowId);

      rowOrder.forEach((rowId) => {
        if (rowMap[itemSetId] && rowMap[itemSetId].rows[rowId]) {
          next.rowMap[itemSetId].rows[rowId].item.number =
            rowOrder.indexOf(rowId) + offset;
        }
      });

      //TODO: Only create a new object for each changed row instead of the entire rowMap
      next.rowMap = { ...rowMap };

      this.setStateAndTrigger(next);
    } else {
      //we can't calculate some derived fields without all row data, so we get the data from the server
      const rowIdsToRefresh = this._getRowIdsInState();

      this._refreshRows(rowIdsToRefresh);
    }
  },

  onItemRowMultipleCellsMutatedRealTime(
    showId,
    payload,
    responsePayload,
    clientId,
  ) {
    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;
    const next = this.getState();

    const { rowMap, itemSetId, columnId } = next;

    if (isPrompter()) {
      /**
       * Only receive changes targeting ItemTitle or activeColumnId
       */
      if (
        !FullScreenCuesheetStoreHelper.hasValidTeleprompterColumnChanges(
          columnId,
          payload.changes,
        )
      )
        return;

      this.setStateAndTrigger(
        FullScreenCuesheetStoreHelper.itemRowMultipleCellsMutatedRealTimeTeleprompter(
          payload.changes,
          next,
        ),
      );
    } else {
      const rowIds = Object.keys(payload.changes);

      rowIds.forEach((id) => {
        const rowChange = payload.changes[id];
        if (rowMap[itemSetId].rows[id]) {
          const newRow = Object.assign({}, rowMap[itemSetId].rows[id]);
          const keys = Object.keys(rowChange);
          keys.forEach((key) => {
            const change = rowChange[key];
            if (key === 'ItemNote' || key === 'ItemDescription') return;
            if (
              key === 'ItemStartTime' ||
              key === 'ItemPresetDuration' ||
              key === 'ItemDuration'
            ) {
              newRow.item = Object.assign(newRow.item, change);
            }
            if (key === 'ItemTitle') {
              newRow.item = Object.assign({}, newRow.item, {
                title: getPlainTextFromRawRTEState(change.rteState),
                editorTitleState: createEditorStateFromRawRTEState(
                  change.rteState,
                ),
              });
            } else {
              newRow.item.cells[key] = {
                editorState: createEditorStateFromRawRTEState(change.rteState),
                text: getPlainTextFromRawRTEState(change.rteState),
              };
            }
          });
          rowMap[itemSetId].rows[id] = newRow;
        }
      });

      this.setInStateAndTrigger('rowMap', { ...rowMap });
    }
  },

  onSetFullScreenFeaturedCell(rowId) {
    const rowIndex = this._getRowIndexByRowId(rowId);
    CuesheetCellActions.setFullScreenFeaturedCell(rowIndex);
  },

  onPopulateActivityAudits(teamId, eventId, showId, rowId, columnId) {
    this.mergeStateAndTrigger({ teamId, eventId, showId, rowId, columnId });
  },

  onPopulateActivityAuditsCompleted(data) {
    this.setInStateAndTrigger('activityAudit', data.items[0]);
  },

  onGetShow(teamId, eventId, showId) {
    this._getShow(teamId, eventId, showId);
    RealTimeActions.subscribeToEvent(eventId);
  },

  onGetShowCompleted(show) {
    this._getShowCompleted(show);
    if (this._onShowPopulateCallback) {
      this._onShowPopulateCallback();
      delete this._onShowPopulateCallback;
    }
  },

  onGetShowForRemotePrompter(teamId, eventId, showId) {
    this._getShow(teamId, eventId, showId);
  },

  onGetShowForRemotePrompterCompleted(show) {
    this._getShowCompleted(show);
  },

  _getShow(teamId, eventId, showId) {
    this.mergeState({ teamId, eventId, showId, userSettings: {} });
    RealTimeActions.subscribeToShow(showId, { teamId, eventId, showId });
    this._trackingContextChanged();
  },

  _getShowCompleted(show) {
    const next = this.getState();
    next.itemSetListId = show.itemSetList;
    next.showId = show._id;
    next.show = show;

    this.setStateAndTrigger(next);
  },

  onPopulateItemSetSubsetBuffered(teamId, eventId, showId, colIds, rowIds) {
    this.setInState('columnId', colIds[0]);
    this.fullScreenObservables.populateItemSetSubsetBuffer.next({
      teamId,
      eventId,
      showId,
      colIds,
      rowIds,
    });
  },

  onPopulateItemSetSubset(teamId, eventId, showId, colIds) {
    const next = Object.assign(this.getState(), { teamId, eventId, showId });
    const nextColumnId = colIds[0];

    if (next.columnId !== nextColumnId) {
      next.loadingColumnData = true;
      next.columnId = nextColumnId;
    }

    this.setStateAndTrigger(next);
  },

  onPopulateItemSetSubsetForGuest(teamId, eventId, showId, colIds) {
    const next = this.getState();
    const nextColumnId = colIds[0];

    if (next.columnId !== nextColumnId) {
      next.loadingColumnData = true;
      next.columnId = nextColumnId;
    }

    this.setStateAndTrigger(next);
  },

  onPopulateItemSetSubsetCompleted(itemSetList) {
    if (!this.getInState('userId')) {
      this._onUserSessionPopulateCallback = () =>
        this._onPopulateItemSetSubsetCompleted(itemSetList);
      UserActions.loadFromSession();
    } else if (!this.getInState('show')) {
      this._onShowPopulateCallback = () =>
        this._onPopulateItemSetSubsetCompleted(itemSetList);
    } else {
      this._onPopulateItemSetSubsetCompleted(itemSetList);
    }
  },

  onPopulateItemSetSubsetForGuestCompleted(itemSetList) {
    this._onPopulateItemSetSubsetCompleted(itemSetList);
  },

  _onPopulateItemSetSubsetCompleted(itemSetList) {
    preloadItemSetImages(itemSetList).then(() => {
      const next = this.getState();

      const userSettings = itemSetList.userSettings[next.userId] || {};
      const mergedUserSettings = next.userSettings
        ? Object.assign(next.userSettings, userSettings)
        : userSettings;
      next.userSettings = mergedUserSettings;

      /**
       * Url query values should hold priority when initally loading the prompter
       * view.
       */
      const query = qs.parse(window.location.search);
      next.userSettings.featuredZoom =
        query.zoom && query.zoom !== 'undefined'
          ? parseInt(query.zoom)
          : ZoomPercentages[ZoomPercentageDefaultIndex];
      next.userSettings.featuredLineHeight =
        query.lineHeight && query.lineHeight !== 'undefined'
          ? parseInt(query.lineHeight)
          : LineHeightPercentages[LineHeightPercentageDefaultIndex];
      next.userSettings.featuredFontWeight =
        query.fontWeight && query.lineHeight !== 'undefined'
          ? query.fontWeight
          : 'regular';
      next.userSettings.featuredCapitalization =
        query.capitalization === 'true' ? true : false;

      next.userSettings.prompterSingleItemEnabled =
        query.singleItem === 'true' ? true : false;
      next.userSettings.prompterItemInfoEnabled =
        query.itemInfo === 'true' ? true : false;
      next.userSettings.prompterRealTimeEditsEnabled =
        next.userSettings.prompterRealTimeEditsEnabled !== undefined
          ? next.userSettings.prompterRealTimeEditsEnabled
          : true;

      next.userSettings.prompterReadArrowEnabled =
        next.userSettings.prompterReadArrowEnabled !== undefined
          ? next.userSettings.prompterReadArrowEnabled
          : true;

      const { columns, defaultColumnOrder } = itemSetList;

      next.columns = this._getFilteredColumns(columns, defaultColumnOrder);
      next.prevColumnTitle = this._getColumnTitle(next.columnId, next);

      const itemSet = itemSetList.itemSets[0];

      const itemSetId = itemSet._id;
      const { rows, rowOrder } = itemSet;

      next.defaultColumnOrder = defaultColumnOrder;
      next.itemSetId = itemSetId;
      next.rowMap =
        next.rowMap && next.rowMap[itemSetId]
          ? next.rowMap
          : { [itemSetId]: { rows: {} } };
      next.rowOrder = rowOrder;

      rows.forEach((row) => {
        row.item = row.item[0];
        const { rteTitleState } = row.item;
        if (rteTitleState) {
          row.item.editorTitleState =
            createEditorStateFromRawRTEState(rteTitleState);
        }
        let { cells } = row.item;
        Object.keys(cells).forEach((key) => {
          const cell = row.item.cells[key];
          if (cell && cell.rteState) {
            row.item.cells[key].editorState = createEditorStateFromRawRTEState(
              cell.rteState,
            );
          } else {
            row.item.cells[key] = {};
          }
        });
        next.rowMap[itemSetId].rows[row._id] = row;
      });

      next.rowMap = Object.assign(
        {},
        FullScreenCuesheetStoreHelper.calculateDerivedFieldsForRowsWrapper(
          next,
          rowOrder,
          next.rowMap,
        ),
      );

      next.loading = false;
      next.loadingPrompter = false;
      next.loadingColumnData = false;

      this.setStateAndTrigger(next);

      if (next.trackedRow && next.trackedRow.itemSetId === itemSetId) {
        this._setTrackingPositionTransient(next.trackedRow);
      }
      FullScreenCuesheetActions.updateEditorDimensions();
    });
  },

  onToggleFullScreen() {
    const { fullScreenEnabled } = this.getState();

    //if a user enables full screen via hotkeys, our action will not detect it, so we check the document
    const userHotkeyEnabled =
      document.fullScreen ||
      document.mozFullScreen ||
      document.webkitIsFullScreen;

    if (fullScreenEnabled || userHotkeyEnabled) {
      this._disableFullScreen();
      this.setInStateAndTrigger('fullScreenEnabled', false);
    } else {
      this._enableFullScreen();
      this.setInStateAndTrigger('fullScreenEnabled', true);
    }
  },

  _enableFullScreen() {
    const element = document.documentElement;

    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  },

  _disableFullScreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  },

  onSetDisplayViewColumn(columnId) {
    const next = this.getState();

    const { rowMap, itemSetId, teamId, eventId, showId } = next;

    let rowIds = [];

    if (rowMap && itemSetId && rowMap[itemSetId].rows) {
      rowIds = Object.keys(rowMap[itemSetId].rows);
    }

    FullScreenCuesheetActions.populateItemSetSubsetBuffered(
      teamId,
      eventId,
      showId,
      [columnId],
      rowIds,
    );
  },

  _getColumnTitle(columnId, next) {
    next = next || this.getState();

    const { columns } = next;

    let columnTitle;

    columns.forEach((col) => {
      if (col._id === columnId) {
        columnTitle = col.title;
      }
    });

    return columnTitle;
  },

  onGetRowOrderCompleted(rowOrder) {
    this.setInStateAndTrigger('rowOrder', rowOrder);
  },

  onToggleTrackingEnabled() {
    const next = this.getState();

    //Ignore if there are no trackable users
    if (!next.trackableUsers) return;

    const willBeEnabled = next.trackingEnabled ? false : true;

    //Set the new tracking value
    this.setInStateAndTrigger('trackingEnabled', willBeEnabled);

    //If tracking is now enabled, update the tracking user
    if (willBeEnabled) {
      const { trackedUserId } = next;
      if (trackedUserId) {
        this._startTrackingSomeone(trackedUserId);
      }
    } else if (next.userId && next.eventId) {
      //Else clear the tracking user

      CuesheetTrackingActions.clearTracking(next.userId, next.eventId);
      CuesheetCueClockActions.clearClock();

      //we reset the scroll indexes to -1 so that when reenabled it will auto scroll
      //regardless of previous scrollToIndex
      next.lastScrollToIndex = -1;
      next.scrollToIndex = -1;

      if (isPrompter()) {
        FullScreenCuesheetActions.clearTrackedRowInRowPanel();
      }

      this.setStateAndTrigger(this._clearRowFieldState('tracked', next));
    }
  },

  onPopulateShowCallersCompleted(result) {
    if (!this.getInState('userId')) {
      this._onUserSessionShowCallerPopulateCallback = () =>
        this._onPopulateShowCallersCompleted(result);
      UserActions.loadFromSession();
    } else {
      this._onPopulateShowCallersCompleted(result);
    }
  },

  _onPopulateShowCallersCompleted(showCallers) {
    const next = this.getState();

    next.trackableUsers = {};

    const user = this._user;

    showCallers.forEach((showCaller) => {
      const { pending, _id } = showCaller;
      if (_id === user._id || pending) return;
      next.trackableUsers[showCaller._id] = _.pick(
        showCaller,
        'firstName',
        'lastName',
        '_id',
        'trackableState',
      );
    });

    if (showCallers.length === 0) {
      delete next.trackedUserId;
      delete next.trackingEnabled;
    } /*
    We do this in the regular cuesheet tracking store, but I'm not sure why
    else if (!next.trackedUserId) {
      next.trackedUserId = Object.keys(next.trackableUsers)[0]
    }
    */

    if (next.trackingEnabled && next.trackedUserId) {
      const { trackedUserId, showId } = next;

      const trackableState = this._getTrackableState(next);

      if (trackableState && trackableState.showId === showId) {
        this._startTrackingSomeoneTransient(trackedUserId, trackableState);
      }
    }

    this.setStateAndTrigger(next);
  },

  onToggleRealTime(bool) {
    this._toggleRealtime(bool);
  },

  _toggleRealtime(bool) {
    const state = this.getState();
    const { userSettings } = state;

    if (userSettings.prompterRealTimeEditsEnabled !== bool) {
      userSettings.prompterRealTimeEditsEnabled = bool;
      this.setInStateAndTrigger(userSettings);
      if (bool) this._refreshRows();
    }
  },

  onShowMutatedRealTime(showId, changes, clientId) {
    const next = this.getState();

    if (!this._shouldAcceptRealtimeUpdates(showId, clientId)) return;

    next.show = Object.assign(next.show, changes);

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onUserRemovedTrackingRealTime(params, payload) {
    const { userId } = params;

    let next = this.getState();

    const { trackedUserId, trackableUsers } = next;

    if (trackableUsers && trackableUsers[userId])
      delete next.trackableUsers[userId].trackableState;

    /**
     * Clear the trackable cue transient state, if the user is tracking the remote user that stopped tracking
     */
    if (userId === trackedUserId) {
      next = this._clearRowFieldState('tracked', next);
    }

    this.setStateAndTrigger(next);
  },

  onCueClockMutatedRealTime(userId, showId, payload) {
    const state = this.getState();

    const nextState = FullScreenCuesheetStoreHelper.cueClockMutatedRealTime(
      userId,
      payload,
      state,
    );

    this.setState(nextState);
  },

  /**
   * Item Row Tracked
   */
  onItemRowTrackedRealTime(userId, showId, payload, responsePayload) {
    if (this._isFullScreen()) {
      const { eventId, itemSetId, itemSetRowId, trackedOn } = payload;
      const user = responsePayload;
      const state = this.getState();
      const { trackableUsers } = state;

      /**
       * If we have received a 'newer' one than the incoming message, do nothing.
       */
      if (this._lastTrackedOn && trackedOn < this._lastTrackedOn) return;

      this._lastTrackedOn = trackedOn;

      if (!trackableUsers[userId]) trackableUsers[userId] = {};
      if (!trackableUsers[userId].trackableState)
        trackableUsers[userId].trackableState = {};
      if (!trackableUsers[userId].trackableState[eventId]) {
        const trackableParams = { showId, itemSetId, itemSetRowId, trackedOn };
        trackableUsers[userId].trackableState[eventId] = trackableParams;
      } else {
        //Update the trackable user state, so that IF the user does track this user later, it will have the right position in state to start tracking
        state.trackableUsers[userId].trackableState[eventId] = {
          showId,
          itemSetId,
          itemSetRowId,
          trackedOn,
        };
      }

      this.setStateAndTrigger(state);

      /**
       * If they aren't tracking...keep moving!
       */
      if (!this.getInState('trackingEnabled')) return;

      //If the eventId isn't the same as the one we're in, or the user isn't the same as the one we're tracking, keep moving!
      if (state.eventId !== eventId || state.trackedUserId !== userId) return;

      this._jumpToTrackingPosition(showId, itemSetId, itemSetRowId, user);
    }
  },

  /**
   * Jump the user to a tracking position
   *
   * 1. If the user is not in the show, switch them to the show and start tracking at the right place in that show
   * 2. If the user IS in the show, track the row transient
   *
   */
  _jumpToTrackingPosition(trackedShowId, itemSetId, itemSetRowId, user) {
    const { showId, eventId, teamId } = this.getState();

    /**
     * We always set the initial tracking position, because if the user loses socket connection, we have to refresh,
     * and we need to retain the tracking position they were at
     */
    CuesheetTrackingActions.setInitialTrackingPosition(
      trackedShowId,
      itemSetId,
      itemSetRowId,
      user,
    );

    /**
     * If the incoming tracked row is in another show, route to that show, else track the row transient
     */
    if (showId !== trackedShowId) {
      if (isDisplayView()) {
        RouteActions.goToDisplayView({
          teamId,
          eventId,
          showId: trackedShowId,
        });
      } else if (isPrompter()) {
        RouteActions.goToTeleprompterInitialization({
          teamId,
          eventId,
          showId: trackedShowId,
        });
      }
    } else {
      this._setTrackingPositionTransient({ itemSetId, rowId: itemSetRowId });
    }
  },

  onSelectTrackableUser(trackedUserId) {
    if (this._isFullScreen()) {
      this.mergeStateAndTrigger({
        trackingEnabled: true,
        trackedUserId,
      });
      this._startTrackingSomeone(trackedUserId);
    }
  },

  /**
   * Get the tracking state of the user you're currently tracking
   */
  _getSelectedTrackableUserTrackingState() {
    const user = this._user;
    const state = this.getState();

    const { trackableUsers } = state;
    const { trackingState, trackedUserId } = user;

    if (trackingState && trackingState[state.eventId]) {
      if (
        trackableUsers[trackedUserId] &&
        trackableUsers[trackedUserId].trackableState[state.eventId]
      ) {
        return trackableUsers[trackedUserId].trackableState[state.eventId];
      }
    }
  },

  /**
   * Helper function used when someone toggles tracking or changes the target user to track
   */
  _startTrackingSomeone(trackedUserId) {
    const user = this._user;
    const { eventId } = this.getState();
    //Persist the tracking state
    CuesheetTrackingActions.updateTrackingUser(
      user._id,
      eventId,
      trackedUserId,
    );

    this._startTrackingSomeoneTransient(trackedUserId);
  },

  /**
   * Helper function to set transient state for tracking (the tracking bar, and the clock)
   */
  _startTrackingSomeoneTransient(trackedUserId, trackableState) {
    trackableState = trackableState || this._getTrackableState();

    if (trackableState) {
      const { showId, itemSetId, itemSetRowId } = trackableState;
      this._populateClock(trackedUserId, trackableState);
      this._jumpToTrackingPosition(showId, itemSetId, itemSetRowId);
    }
  },

  _getTrackableState(next) {
    next = next || this.getState();

    const { trackedUserId, trackableUsers } = next;

    if (
      trackableUsers[trackedUserId] &&
      trackableUsers[trackedUserId].trackableState &&
      trackableUsers[trackedUserId].trackableState[next.eventId]
    ) {
      const trackableState =
        trackableUsers[trackedUserId].trackableState[next.eventId];

      return trackableState;
    }
  },

  _populateClock(userId, trackableState) {
    const { eventId, showId } = this.getState();

    if (trackableState && trackableState.showId === showId) {
      CuesheetCueClockActions.populate(
        userId,
        eventId,
        showId,
        fromJS(trackableState.cueClock),
      );
    } else {
      //Clear the clock, which is necessary in cases where you've switched shows,
      //and the clock was active on the previous show
      CuesheetCueClockActions.clearClock();
    }
  },

  _setTrackingPositionTransient({ itemSetId, rowId }) {
    let next = this.getState();

    const { rowMap, trackingEnabled } = next;

    const trackedRow = {
      itemSetId,
      rowId,
    };

    next.trackedRow = trackedRow;

    const trackedRowIndex = this._getRowIndexByRowId(rowId, next);

    if (trackedRowIndex === -1) return this.setStateAndTrigger(next);

    //clear existing tracked status if it exists
    next = this._clearRowFieldState('tracked', next);

    //if the row id isn't currently present in the rowMap, it will get loaded
    //once the user begins scrolling to it
    if (
      rowMap &&
      rowMap[itemSetId] &&
      rowMap[itemSetId].rows[rowId] &&
      trackingEnabled
    ) {
      rowMap[itemSetId].rows[rowId] = {
        ...rowMap[itemSetId].rows[rowId],
        tracked: true,
      };
      //TODO: Only create a new object for each changed row instead of the entire rowMap
      next.rowMap = rowMap;
    }

    if (isDisplayView()) {
      //only scroll to the row if tracking is enabled
      if (trackingEnabled) {
        next = this._setScrollIndexToTrackedRow(trackedRowIndex, rowId, next);
      }
    }

    this.setStateAndTrigger(next);

    if (isPrompter()) {
      FullScreenCuesheetActions.scrollToRowIdInPrompter(rowId);
      FullScreenCuesheetActions.updateTrackedRowInRowPanel(trackedRow);
    }
  },

  /**
   * Get the order, including hidden columns
   */
  _getFullColumnOrder(itemSetList, userId) {
    return (
      itemSetList.userColumnOrder[userId] || itemSetList.defaultColumnOrder
    );
  },

  _getFilteredColumns(columns, fullColumnOrder) {
    const filteredColumns = fullColumnOrder.map((colId) =>
      columns.find((col) => col._id === colId),
    );
    return filteredColumns;
  },

  onAddColumn(column) {
    const { teamId, eventId, showId, itemSetListId } = this.getState();

    FullScreenCuesheetActions.addColumnPersistent(
      teamId,
      eventId,
      showId,
      itemSetListId,
      column,
    );
  },

  onAddColumnPersistentCompleted(data) {
    const { column } = data;
    this._addColumnTransient(column);
    FullScreenCuesheetActions.saveFeaturedColumn(column._id);
  },

  onAddColumnPersistentFailed() {
    ToastActions.failed('Failed to create new column.');
  },

  onGoToTeleprompterInitialization(params) {
    const next = this.getState();

    const { showId } = next;

    if (params.showId !== showId) {
      delete next.rowMap;
      next.loading = true;
      this.setStateAndTrigger(next);
    }
  },

  onSetForceOnboardingWelcome(bool) {
    this.setInStateAndTrigger('forceOnboardingWelcome', bool);
  },

  _routeToTeleprompter(params, query) {
    const next = this.getState();

    const { teamId, eventId, showId, rowOrder, userSettings } = next;
    const rowId = params.rowId ? params.rowId : rowOrder[0];
    const columnId = params.columnId
      ? params.columnId
      : this._getDefaultColumnId(next);

    const defaultParams = Object.assign(
      {},
      {
        teamId,
        eventId,
        showId,
        rowId,
        columnId,
      },
      params,
    );

    const defaultQuery = Object.assign(
      {},
      populatePrompterSettingsWithDefaults(userSettings),
      query,
    );

    RouteActions.goToTeleprompter(defaultParams, defaultQuery);
  },

  onUpdateScrollIndex(scrollToIndex) {
    this.mergeStateAndTrigger({
      scrollToIndex,
      lastScrollToIndex: scrollToIndex,
    });
  },

  onResetLastScrollIndex() {
    this.mergeStateAndTrigger({
      lastScrollToIndex: LastScrollToIndexDefault,
    });
  },

  onUpdateActiveRowInRowPanel(rowId) {
    urlHelper.updateRowIdInUrl(rowId);
    const next = this.getState();
    const previousRowId = next.rowId;
    const previousRowIndex = next.rowOrder.indexOf(previousRowId);
    const nextRowIndex = next.rowOrder.indexOf(rowId);

    next.rowId = rowId;
    let triggerState = false;

    /**
     * Any changes that happen above the the active rowIndex are cached until
     * user scrolls above the active index. Once the user scrolls above the
     * active row index the cached changes are applied.
     *
     * This prevents the arrow position from jumping to different positions
     * in the row feed while prompter is in auto scroll. This jumping is due to
     * new changes being added or removed thus causing row height changes.
     *
     * See issue: https://github.com/SHOFLO/Issues/issues/4777
     *
     */
    if (nextRowIndex < previousRowIndex) {
      if (next.cachedRowMap) {
        triggerState = true;
        next.rowMap = next.cachedRowMap;
        delete next.cachedRowMap;
      }

      if (next.cachedChangedRows) {
        triggerState = true;
        next.changedRows = next.cachedChangedRows;
        delete next.cachedChangedRows;
      }
      if (next.cachedRowOrder) {
        triggerState = true;
        next.rowOrder = next.cachedRowOrder;
        delete next.cachedRowOrder;
      }
    }

    if (triggerState) {
      this.setStateAndTrigger(next);
    } else {
      this.setState(next);
    }
  },

  _setScrollIndexToTrackedRow(trackedRowIndex, itemSetRowId, state) {
    const next = state || this.getState();

    if (!next.trackingEnabled) return next;

    let nextScrollToIndex = trackedRowIndex - 1;
    if (nextScrollToIndex < 1) nextScrollToIndex = 0;

    next.scrollToIndex = nextScrollToIndex;

    return next;
  },

  onSaveDataCellChanges(rowId, columnId, editorState) {
    const next = this.getState();
    const { teamId, eventId, showId, itemSetListId, itemSetId, userId } = next;

    const transientCellChanges = {
      editorState,
      format: this._getCellFormat(rowId, columnId, next),
      userId,
    };

    const rteState = convertToRaw(editorState.getCurrentContent());

    const persistentCellChanges = {
      rteState,
      format: this._getCellFormat(rowId, columnId, next),
      userId,
    };

    const changes = { [rowId]: { [columnId]: persistentCellChanges } };

    this.saveDataCellChangesTransient(rowId, columnId, transientCellChanges);
    FullScreenCuesheetActions.saveDataCellChangesPersistent(
      teamId,
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      changes,
    );
  },

  saveDataCellChangesTransient(rowId, columnId, changes) {
    let next = this.getState();

    const cell = this._getCell(rowId, columnId, next);

    next = this._setCellTransient(
      rowId,
      columnId,
      this._mergeCellChangesTransient(cell, changes, columnId),
      next,
    );

    const changedRows = next.changedRows || {};

    let rowIndex = this._getRowIndexByRowId(rowId, next);

    changedRows[rowIndex] = rowIndex;

    next.changedRows = changedRows;

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onSaveDataCellChangesPersistentCompleted(data) {
    ToastActions.saved();

    const { teamId, eventId, showId, rowId, columnId } = this.getState();

    FullScreenCuesheetActions.populateActivityAudits(
      teamId,
      eventId,
      showId,
      rowId,
      columnId,
      1,
      1,
    );
  },

  onSaveDataCellChangesPersistentFailed() {
    ToastActions.failed('Save data cell changes failed');
  },

  _alertActionFailed() {
    //TODO: Handle this the correct way like regular cuesheet
    console.log('Failed to save data');
  },

  onCellSelect(rowId, columnId) {
    //Only supports single cell selection
    this.setInStateAndTrigger('selectedCells', {
      [`${rowId}_${columnId}`]: true,
    });
  },

  onCellEdit(rowId, columnId) {
    this.setInStateAndTrigger('editingCell', { rowId, columnId });
  },

  onClearEditing() {
    const next = this.getState();
    next.editingCell = {};
    this.setStateAndTrigger(next);
  },

  _getRowIndexByRowId(rowId, state) {
    const next = state || this.getState();
    return next.rowOrder.findIndex((id) => id === rowId);
  },

  _getRowIdByRowIndex(rowIndex, state) {
    const next = state || this.getState();
    return next.rowOrder[rowIndex];
  },

  _getRowIdsInState() {
    const { itemSetId, rowMap } = this.getState();

    const rowIds =
      rowMap && rowMap[itemSetId] ? Object.keys(rowMap[itemSetId].rows) : [];

    return rowIds;
  },

  onRefreshAllRows() {
    this._refreshRows();
  },

  _refreshRows(rowIds = []) {
    const { teamId, eventId, showId, columnId } = this.getState();

    const columnIds = columnId ? [columnId] : [];

    FullScreenCuesheetActions.populateItemSetSubsetBuffered(
      teamId,
      eventId,
      showId,
      columnIds,
      rowIds,
    );
  },

  _clearRowFieldState(field, state) {
    const next = state || this.getState();

    const { rowMap, itemSetId } = next;

    if (!rowMap || !itemSetId) return next;

    const rowIds = Object.keys(rowMap[itemSetId].rows);

    rowIds.forEach((rowId) => {
      if (rowMap[itemSetId].rows[rowId][field]) {
        delete next.rowMap[next.itemSetId].rows[rowId][field];
      }
    });

    //TODO: Only create a new object for each changed row instead of the entire rowMap
    next.rowMap = { ...rowMap };

    return next;
  },

  _deleteItemRows(rowIds) {
    const next = this.getState();

    rowIds.forEach((rowId) => {
      delete next.rowMap[next.itemSetId].rows[rowId];
    });

    //Make sure the component re-renders
    next.rowMap = { ...next.rowMap };

    this.setStateAndTrigger(next);
  },

  _updateItemRow(rowId, changes) {
    let updateData = {};

    if (changes.rteTitleState) updateData.rteTitleState = changes.rteTitleState;
    if (updateData.rteTitleState) {
      updateData.title = getPlainTextFromRawRTEState(updateData.rteTitleState);
    }

    const next = this.getState();
    const { rowMap, itemSetId } = next;

    const row = rowMap[itemSetId].rows[rowId];

    if (!row) return next;

    next.rowMap[itemSetId].rows[rowId].item = Object.assign(
      {},
      next.rowMap[itemSetId].rows[rowId].item,
      updateData,
    );

    next.rowMap = Object.assign({}, next.rowMap);

    return next;
  },

  _setCellTransient(rowId, columnId, cellData, state) {
    const next = state || this.getState();
    const { rowMap, itemSetId } = next;

    const row = rowMap[itemSetId].rows[rowId];

    if (!row) return next;

    rowMap[itemSetId].rows[rowId].item.cells[columnId] = cellData;

    next.rowMap = { ...rowMap };

    return next;
  },

  _getCell(rowId, columnId, state) {
    const { rowMap, itemSetId } = state || this.getState();
    return rowMap[itemSetId].rows[rowId].item.cells[columnId];
  },

  _getCellFormat(rowId, columnId, state) {
    const cellData = this._getCell(rowId, columnId, state);
    return cellData && cellData.format ? cellData.format : {};
  },

  _mergeCellChangesTransient(cell, changes) {
    if (!cell) cell = {};
    const { editorState } = changes;

    if (editorState) {
      cell.text = getPlainTextFromRawRTEState(
        convertToRaw(editorState.getCurrentContent()),
      );
    }

    return Object.assign({}, cell, changes);
  },

  /**
   * Helper for checking if item row is locked and throwing an alert
   *
   * If item is locked then throw an alert and return true.
   *
   * @returns {Boolean}
   */
  getItemRowLockStatusWithAlert(rowIndex) {
    if (this.getItemRowLockStatus(rowIndex)) {
      AlertActions.show(Messages.lockedRowModal, UIRegions.Cuesheet);
      return true;
    }
    return false;
  },

  /**
   * Helper for checking if item row is locked
   *
   * @returns {Boolean}
   */
  getItemRowLockStatus(rowIndex) {
    const { itemSetId, rowMap } = this.getState();
    const rowId = this._getRowIdByRowIndex(rowIndex);

    return rowMap[itemSetId].rows[rowId].item.locked;
  },

  onTogglePanel(panel, display) {
    this.setInStateAndTrigger(panel, display);
  },

  onDisableAutoScrolling() {
    this.mergeStateAndTrigger({
      autoScrollEnabled: false,
    });
  },

  onToggleAutoScrolling() {
    const { autoScrollEnabled } = this.getState();
    this.mergeStateAndTrigger({
      autoScrollEnabled: !autoScrollEnabled,
    });
  },

  onStartScrolling() {
    this.mergeStateAndTrigger({
      autoScrollEnabled: true,
    });
  },

  onStopScrolling() {
    this.mergeStateAndTrigger({
      autoScrollEnabled: false,
    });
  },

  onToggleReadArrow(enabled) {
    this.setInStateAndTrigger('readArrowEnabled', enabled);
  },

  onToggleItemInfo(enabled) {
    this.setInStateAndTrigger('itemInfoEnabled', enabled);
  },

  onToggleSingleItem(enabled) {
    this.setInStateAndTrigger('singleItemEnabled', enabled);
  },

  _getUserSettingKey(type, settings) {
    const next = this.getState();
    const { userSettings } = next;
    let userSetting = userSettings[type];
    let settingKey = Object.keys(settings).find(
      (key) => settings[key] === userSetting,
    );

    return settingKey;
  },

  onIncrementLineHeight() {
    let lineHeightKey = this._getUserSettingKey(
      'featuredLineHeight',
      LineHeightPercentages,
    );

    lineHeightKey++;

    if (LineHeightPercentages[lineHeightKey]) {
      this._saveUserSettingsChanges(
        'featuredLineHeight',
        LineHeightPercentages[lineHeightKey],
      );
    }
  },

  onDecrementLineHeight() {
    let lineHeightKey = this._getUserSettingKey(
      'featuredLineHeight',
      LineHeightPercentages,
    );

    lineHeightKey--;

    if (LineHeightPercentages[lineHeightKey]) {
      this._saveUserSettingsChanges(
        'featuredLineHeight',
        LineHeightPercentages[lineHeightKey],
      );
    }
  },

  onIncrementZoom() {
    let zoomKey = this._getUserSettingKey('featuredZoom', ZoomPercentages);

    zoomKey++;

    if (ZoomPercentages[zoomKey]) {
      this._saveUserSettingsChanges('featuredZoom', ZoomPercentages[zoomKey]);
    }
  },

  onDecrementZoom() {
    let zoomKey = this._getUserSettingKey('featuredZoom', ZoomPercentages);

    zoomKey--;

    if (ZoomPercentages[zoomKey]) {
      this._saveUserSettingsChanges('featuredZoom', ZoomPercentages[zoomKey]);
    }
  },

  onIncreaseFeaturedAutoScrollSpeed() {
    const currentScrollSpeedIndex = this.getInState('autoScrollSpeedIndex');

    if (currentScrollSpeedIndex === AutoScrollSpeeds.length - 1) return;

    const nextScrollSpeedIndex = currentScrollSpeedIndex + 1;

    this.setInStateAndTrigger('autoScrollSpeedIndex', nextScrollSpeedIndex);
  },

  onDecreaseFeaturedAutoScrollSpeed() {
    const currentScrollSpeedIndex = this.getInState('autoScrollSpeedIndex');

    if (currentScrollSpeedIndex === 0) return;

    const nextScrollSpeedIndex = currentScrollSpeedIndex - 1;

    this.setInStateAndTrigger('autoScrollSpeedIndex', nextScrollSpeedIndex);
  },

  //this listens to CuesheetColumnActions a change in in the featured column in featured view
  onSaveFeaturedCuesheetColumn(columnId) {
    if (isPrompter()) this._setFeaturedColumnTransient(columnId);
  },

  onSaveFeaturedColumn(columnId) {
    const { teamId, eventId, showId, itemSetListId, userId } = this.getState();

    this._setFeaturedColumnTransient(columnId);

    FullScreenCuesheetActions.saveFeaturedColumnPersistent(
      teamId,
      eventId,
      showId,
      itemSetListId,
      userId,
      { featuredColumn: columnId },
    );
  },

  _setFeaturedColumnTransient(columnId) {
    const next = this.getState();

    if (!next.userSettings) next.userSettings = {};
    next.userSettings.featuredColumn = columnId;

    next.columnId = columnId;
    next.prevColumnTitle = this._getColumnTitle(columnId, next);

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onSaveFeaturedColumnPersistentCompleted(data) {
    const { rowId } = this.getState();
    const params = { rowId } || {};

    this._routeToTeleprompter(params, {});
  },

  onSetFeaturedAutoScrollSpeed(featuredSpeed) {
    this.setInStateAndTrigger('autoScrollSpeedIndex', featuredSpeed);

    this.onStartScrolling();
  },

  onWidenPrompterWidth() {
    const currentPrompterWidthIndex = this.getInState('prompterWidthIndex');

    if (currentPrompterWidthIndex === PrompterWidths.length - 1) return;

    const nextPrompterWidthIndex = currentPrompterWidthIndex + 1;

    FullScreenCuesheetActions.setPrompterWidth(nextPrompterWidthIndex);
  },

  onNarrowPrompterWidth() {
    const currentPrompterWidthIndex = this.getInState('prompterWidthIndex');

    if (currentPrompterWidthIndex === 0) return;

    const nextPrompterWidthIndex = currentPrompterWidthIndex - 1;

    FullScreenCuesheetActions.setPrompterWidth(nextPrompterWidthIndex);
  },

  onSetPrompterWidth(width) {
    this.setInStateAndTrigger('prompterWidthIndex', width);
    FullScreenCuesheetActions.updateEditorDimensions();
    if (isTeleprompter()) {
      RemoteTeleprompterActions.emitOperatorSettings({
        prompterWidthIndex: width,
      });
    }
  },

  onSaveUserSettings(field, value, params, query) {
    this._saveUserSettingsChanges(field, value, params, query);
  },

  onUpdateUserSettingsPersistentCompleted(userSettings) {
    const { prompterRealTimeEditsEnabled } = userSettings;
    if (typeof prompterRealTimeEditsEnabled === 'boolean') {
      this._toggleRealtime(prompterRealTimeEditsEnabled);
    }
  },

  _saveUserSettingsChanges(field, value) {
    /**
     * Update url query params to match userSettings
     */
    let queryField = '';
    if (field === 'featuredFontWeight') queryField = 'fontWeight';
    if (field === 'featuredLineHeight') queryField = 'lineHeight';
    if (field === 'featuredZoom') queryField = 'zoom';
    if (field === 'featuredCapitalization') queryField = 'capitalization';
    if (field === 'prompterItemInfoEnabled') queryField = 'itemInfo';
    if (field === 'prompterSingleItemEnabled') queryField = 'singleItem';
    if (field === 'prompterRealTimeEditsEnabled')
      queryField = 'prompterRealTimeEditsEnabled';
    if (queryField) urlHelper.updateQueryInUrl(queryField, value);

    const next = this.getState();
    const { teamId, eventId, showId, itemSetListId, userId } = next;

    if (!next.userSettings) next.userSettings = {};

    // we set prompterRealTimeEditsEnabled inside of _toggleRealtime
    // via onUpdateUserSettingsPersistentCompleted. we don't want the
    // state to update until the setting is saved
    if (field !== 'prompterRealTimeEditsEnabled') {
      next.userSettings[field] = value;
    }

    /**
     * Prevent requests for the same field happening in quick succession.
     * For instance, with a dragable field like FontSize
     */
    if (this[`_save${field}`]) {
      clearTimeout(this[`_save${field}`]);
      delete this[`_save${field}`];
    }

    this[`_save${field}`] = setTimeout(() => {
      CuesheetColumnActions.updateUserSettingsPersistent(
        teamId,
        eventId,
        showId,
        itemSetListId,
        userId,
        { [field]: value },
      );
    }, 300);

    this.setStateAndTrigger(next);
  },

  onClearChangedRows() {
    const nextState = this.getState();
    delete nextState.changedRows;
    this.setStateAndTrigger(nextState);
  },

  _onRemoteTeleprompterStoreChange({
    operatorClientId,
    prompterRealTimeEditsEnabled,
  }) {
    if (isRemoteTeleprompter()) {
      const nextState = this.getState();
      nextState.operatorClientId = operatorClientId;
      nextState.remoteRealtimeEnabled = prompterRealTimeEditsEnabled;
      this.setState(nextState);
    }
  },

  _shouldAcceptRealtimeUpdates(showId, clientId) {
    if (this.getInState('showId') !== showId) return false;
    const state = this.getState();
    const { operatorClientId, remoteRealtimeEnabled, userSettings } = state;
    if (isTeleprompter()) {
      const { prompterRealTimeEditsEnabled } = userSettings;
      return prompterRealTimeEditsEnabled;
    }
    if (isRemoteTeleprompter()) {
      return remoteRealtimeEnabled || operatorClientId === clientId;
    }
    return true;
  },
});
